import { Table, Paragraph } from 'canoe';
import { connectWithHammock, ENDPOINTS } from 'hammock';
import PropTypes from 'prop-types';
import React from 'react';
import { s3Image } from 'site-constants';
import styled from 'styled-components';

import { ContentMetadata } from '@candu-ai/content/types';
import { useNavigation } from 'commons/hooks';
import { CONTENT_PUBLISHED_STATES } from 'commons/models';
import ContentPublishedState from 'dashboard/pages/Content/components/ContentPublishedState';
import { contentPath } from 'dashboard/pages/Content/constants';

const EmptyState = styled.div`
  align-items: center;
  flex: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
`;

interface ParentDocument {
  id: string;
  contentId: string;
  segmentId: string;
  matchMetadata: {
    embeddedContentId: string;
  };
}

interface Props {
  parentDocuments: ParentDocument[];
  contentMetadata: ContentMetadata[];
  segmentDefinitions: any[];
}

const getPublishedState = (content: any) => {
  if (content?.archivedAt) {
    return CONTENT_PUBLISHED_STATES.ARCHIVED;
  }
  if (content?.visibilityState === 'Off') {
    return CONTENT_PUBLISHED_STATES.DRAFT;
  }
  if (content?.lastPublishedAt) {
    return new Date(content.lastPublishedAt) >= new Date(content.lastModifiedAt)
      ? CONTENT_PUBLISHED_STATES.PUBLISHED
      : CONTENT_PUBLISHED_STATES.UNSYNCED;
  }

  return CONTENT_PUBLISHED_STATES.DRAFT;
};

const ContentEmbedTable = ({ parentDocuments, contentMetadata, segmentDefinitions }: Props) => {
  const navigate = useNavigation();

  const mapDocuments = parentDocuments.map((document) => ({
    ...document,
    index: document.id,
    content: contentMetadata.find((content) => content.id === document.contentId),
    segmentName:
      document.segmentId === 'Everyone'
        ? 'Everyone'
        : segmentDefinitions.find(
            (segmentDefinition) => segmentDefinition.id === document.segmentId,
          )?.name,
  }));

  if (parentDocuments.length > 0) {
    return (
      <Table
        onRow={(record) => ({
          onClick: () => navigate(contentPath(record.contentId, record.segmentId)),
        })}
        rows={mapDocuments}
        columns={[
          {
            key: 'name',
            dataIndex: 'name',
            title: 'Parent name',
            render: (text, record: any) => record.content?.name,
          },
          {
            key: 'status',
            dataIndex: 'lastPublishedAt',
            title: 'Status',
            render: (text, record: any) => (
              <ContentPublishedState
                semaphoreOnly={false}
                content={{
                  ...record.content,
                  publishedState: getPublishedState(record.content),
                }}
              />
            ),
          },
          {
            key: 'segmentName',
            dataIndex: 'segmentName',
            title: 'Segment',
          },
        ]}
      />
    );
  }

  return (
    <EmptyState>
      <img src={s3Image('/svg/tutorials-empty-placeholder.svg')} alt="No embeds yet added." />
      <Paragraph>No embeds yet added.</Paragraph>
    </EmptyState>
  );
};

ContentEmbedTable.propTypes = {
  parentDocuments: PropTypes.array,
  contentMetadata: PropTypes.array,
  segmentDefinitions: PropTypes.array,
};

export default connectWithHammock({
  endpoint: ENDPOINTS.segmentsDefinition,
  resourceName: 'segmentDefinitions',
})(ContentEmbedTable);
